// Bella Vita frontend — i18n runtime.
// - Arabic is primary (default). English secondary.
// - Language choice is persisted (localStorage) and survives reloads.
// - setLang() updates <html lang> and <html dir> — no browser-translate hacks.
// - Subscribers (the router) re-render on change.

import ar from './ar.js';
import en from './en.js';
import { CONFIG } from '../config.js';

const DICTS = { ar, en };
const STORE_KEY = 'bv.lang';
const subs = new Set();

function readStored() {
  try {
    const v = localStorage.getItem(STORE_KEY);
    return CONFIG.supportedLangs.includes(v) ? v : null;
  } catch {
    return null;
  }
}

let current = readStored() || CONFIG.defaultLang;

/** Current language code ('ar' | 'en'). */
export function getLang() {
  return current;
}

export function getDir(lang = current) {
  return DICTS[lang]?._dir || 'ltr';
}

/**
 * Pick the current-language string from a localised data object `{ ar, en }`
 * (used for adapter/service data — service names, category names, etc.).
 * Falls back across languages, then to '' — never throws.
 */
export function localize(obj, lang = current) {
  if (obj == null) return '';
  if (typeof obj === 'string') return obj;
  return obj[lang] ?? obj.ar ?? obj.en ?? '';
}

/** Dotted-path lookup, e.g. t('booking.party_title'). Falls back to the key. */
export function t(path, lang = current) {
  const dict = DICTS[lang] || DICTS.ar;
  const hit = String(path).split('.').reduce((o, k) => (o == null ? o : o[k]), dict);
  if (hit != null) return hit;
  // fall back to the other language before giving up
  const alt = String(path).split('.').reduce((o, k) => (o == null ? o : o[k]), DICTS.ar);
  return alt != null ? alt : path;
}

/** Apply <html lang/dir>. Safe to call repeatedly (used on every route change). */
export function applyDocumentLang(lang = current) {
  const doc = document.documentElement;
  doc.setAttribute('lang', lang);
  doc.setAttribute('dir', getDir(lang));
}

export function setLang(lang) {
  if (!CONFIG.supportedLangs.includes(lang) || lang === current) {
    applyDocumentLang(current);
    return;
  }
  current = lang;
  try {
    localStorage.setItem(STORE_KEY, lang);
  } catch {
    /* private mode / storage disabled — language still works for this session */
  }
  applyDocumentLang(lang);
  subs.forEach((fn) => fn(lang));
}

export function toggleLang() {
  setLang(current === 'ar' ? 'en' : 'ar');
}

/** Subscribe to language changes; returns an unsubscribe fn. */
export function onLangChange(fn) {
  subs.add(fn);
  return () => subs.delete(fn);
}
